import { createContext, FC, useLayoutEffect, useState } from 'react';

import { tracker } from 'services';
import { useRouter } from 'hooks';
import { SideNav } from 'components';

import LayoutBackground from './Layout.Background';
import Content, { LayoutContentProps } from './Layout.Content';
import Footer, { LayoutFooterProps } from './Layout.Footer';
import Header, { LayoutHeaderProps } from './Layout.Header';

import styles from './Layout.module.scss';

export type LayoutComposition = {
  Header: FC<LayoutHeaderProps>;
  Content: FC<LayoutContentProps>;
  Footer: FC<LayoutFooterProps>;
};

export type LayoutProps = {
  withBackground?: boolean;
};

export type LayoutContext = {
  isSidebarOpen: boolean;
  openProfile(): void;
  openSidebar(): void;
  closeSidebar(): void;
};

export const layoutContext = createContext<LayoutContext>({
  isSidebarOpen: false,
  openProfile: () => {},
  openSidebar: () => {},
  closeSidebar: () => {}
});

const Layout: FC<LayoutProps> & LayoutComposition = ({ children, withBackground = false }): JSX.Element => {
  const router = useRouter();

  const [state, setState] = useState({
    isSidebarOpen: false,
    isProfileOpen: false
  });

  useLayoutEffect(() => {
    if (state.isSidebarOpen) {
      document.body.classList.add('inactive');
    } else {
      document.body.classList.remove('inactive');
    }
  }, [state.isSidebarOpen]);

  const openProfile = () => {
    tracker.openProfilePanel();
    router.push('/profile');
  };

  const openSidebar = () => {
    tracker.openSidebar();
    setState(prev => ({ ...prev, isSidebarOpen: true }));
  };
  const closeSidebar = () => setState(prev => ({ ...prev, isSidebarOpen: false }));

  return (
    <layoutContext.Provider
      value={{
        isSidebarOpen: state.isSidebarOpen,
        openProfile,
        openSidebar,
        closeSidebar
      }}
    >
      <div className={styles.wrapper}>
        <SideNav isOpen={state.isSidebarOpen} onClose={closeSidebar} />
        {withBackground && <LayoutBackground />}
        {children}
      </div>
    </layoutContext.Provider>
  );
};

Layout.Header = Header;
Layout.Content = Content;
Layout.Footer = Footer;

export default Layout;
